import {priceToString} from "../../util/PriceUtil";
import React from "react";
import {PurchaseOption, SelectedBooking} from "./BookingFlow";
import {longDateFormat, shortDateTimeFormat2, shortTimeFormat} from "../../util/DateTimeUtil";
import {IApiBusinessProfileProto, space} from "../../compiled";

type Props = {
  businessProfile: IApiBusinessProfileProto
  selectedBooking: SelectedBooking,
  purchaseOption?: PurchaseOption,
}

/**
 * This is the unified left nav that's shown after an appointment or class has been
 * fully selected.
 *
 */
export const BookingLeftNav = (props: Props) => {

  const BookingInfo = () => {

    if (props.selectedBooking.class) {
      const clazz = props.selectedBooking.class;
      const startTime = new Date(clazz.startTimestamp as number);
      const endTime = new Date(clazz.endTimestamp as number);

      return <>
        <div>
          <div><b>{clazz.className}</b></div>
          <div>with {clazz.teacherName}</div>
        </div>

        <div>
          <div><b>{shortTimeFormat(startTime)} - {shortTimeFormat(endTime)}</b></div>
          <div>{longDateFormat(startTime)}</div>
        </div>

        <div>
          <div><b>{props.businessProfile.proto?.businessName}</b></div>
          <div>{clazz.locationName}</div>
          <div>{clazz.locationAddress}</div>
        </div>
      </>
    }

    if (props.selectedBooking.appointment) {
      const appt = props.selectedBooking.appointment;

      const startTime = new Date(appt.timestamp as number);
      const lengthMinutes = appt.service.lengthMinutes as number;
      const endTime = new Date(appt.timestamp as number + lengthMinutes * 60 * 1000);

      const service = appt.service;
      const practitioner = appt.practitioner;

      return <>
        <div>
          <div><b>{service.name}</b></div>
          <div>with {practitioner.firstName} {practitioner.lastName}</div>
        </div>

        <div>
          <div><b>{shortTimeFormat(startTime)} - {shortTimeFormat(endTime)}</b></div>
          <div>{longDateFormat(startTime)}</div>
        </div>

        <div>
          <div><b>{props.businessProfile.proto?.businessName}</b></div>
          {appt.location && <>
            <div>{appt.location.locationName}</div>
            <div>{appt.location.physicalLocation?.address}</div>
          </>}
          {appt.virtualAppt && <>
            <div>Virtual Appointment</div>
            <div>Meet with your practitioner via Zoom</div>
          </>}
        </div>
      </>
    }

    return <></>
  }

  const getPurchaseOptionName = () => {
    if (!props.purchaseOption) {
      return <></>
    }
    if (props.purchaseOption.buySingleCredit) {
      let credit = props.purchaseOption?.buySingleCredit;
      return 'Single Credit: ' + (credit?.service?.name || credit?.class?.name || credit?.priceCategory?.displayName);
    }
    if (props.purchaseOption.buyNewClassPackage) {
      return 'New Package: ' + props.purchaseOption?.buyNewClassPackage?.displayName;
    }
    const credit = props.purchaseOption.useExistingClassCredit;
    if (props.purchaseOption.useExistingClassCredit) {
      return 'Existing Credit: ' + (credit?.serviceDetails?.serviceName || credit?.classDetails?.className || credit?.priceCategoryDetails?.displayName);
    }
    if (props.purchaseOption.useExistingClassPackage) {
      return 'Existing Package: ' + props.purchaseOption.useExistingClassPackage.packageDetails?.displayName;
    }
    if (props.purchaseOption.postPaid) {
      return 'Reservation only';
    }
    return '';
  }

  const getPrice = () => {
    if (!props.purchaseOption) {
      return <></>
    }
    if (props.purchaseOption.buySingleCredit) {
      let credit = props.purchaseOption?.buySingleCredit;
      return priceToString(credit?.service?.paymentPolicy?.prepaid?.price || credit?.class?.paymentPolicy?.prepaid?.price || credit?.priceCategory?.singleClassPrice);
    }
    if (props.purchaseOption.buyNewClassPackage) {
      return priceToString(props.purchaseOption.buyNewClassPackage.price);
    }
    if (props.purchaseOption.useExistingClassCredit) {
      return "Free"
    }
    if (props.purchaseOption.useExistingClassPackage) {
      return props.purchaseOption.useExistingClassPackage.classesRemaining + ' credits remaining';
    }
    if (props.purchaseOption.postPaid) {
      return 'Pay the provider following your appointment'
    }

    return '';
  }

  const PaymentInfo = () => {
    return <>
      {props.purchaseOption && <div>
        <div><b>{getPurchaseOptionName()}</b></div>
        <div>{getPrice()}</div>
      </div>}
    </>;
  }

  return <div className={'BookingOverlayBottomLeft BookingOverlaySectionMargin'}>
    <BookingInfo/>
    <PaymentInfo/>
  </div>
}
